import { EXAMPLES } from '../demo.js';
import { html, SP } from '../html.js';
import type { VNode } from '../html.js';
import type { Actions } from '../actions.js';
import type { ConsoleState } from '../store.js';
import type { Ownership } from '../types.js';

/**
 * One of two assertions, sent with the crawl.
 *
 * "Someone else's site" is the pre-selected one because it is the assertion
 * that adds the disclaimer. The other way round, a visitor who never read the
 * choice would have a stranger's brand crawled as their own property — and the
 * server would only catch it at publish time, on a draft already headed as if
 * it were true. The manifest's `policy.ownership` is what the gate reads back.
 */
function OwnershipChoice(props: {
  value: Ownership;
  current: Ownership;
  head: string;
  body: VNode;
  onPick: (value: Ownership) => void;
}): VNode {
  const on = props.value === props.current;
  return html`
    <label class="ack" style="border-top:0;padding:8px 0">
      <input
        type="radio"
        name="ownership"
        value=${props.value}
        checked=${on}
        style="margin:3px 0 0;accent-color:var(--signal)"
        onChange=${() => props.onPick(props.value)}
      />
      <span class="ack__text"><b style="color:var(--text)">${props.head}</b> — ${props.body}</span>
    </label>
  `;
}

/**
 * One input, one button.
 *
 * The hero IS the field — the most characteristic thing in this product's world
 * is the moment a URL turns into a crawl, so nothing gets to sit above it
 * except the sentence that says what comes back.
 *
 * The third block on the page is the refusal. Stating up front that tools get
 * dropped, with the real numbers from a real run, is the strongest thing this
 * product can say, and burying it under the feature panes would waste it.
 */
export function LandingScreen(props: { state: ConsoleState; actions: Actions }): VNode {
  const { state, actions } = props;
  /**
   * Live and signed out, the button leads to the sign-in step and not to a
   * crawl, so it must not say "Crawl it". A button that names an act it does
   * not perform is the same lie as a progress bar that moves on its own, and
   * this console does not tell that one either.
   */
  const gated = state.mode === 'live' && state.principalChecked && !state.principal;
  const resumeHost = hostOf(state.resumeUrl);

  return html`
    <div>
      <header class="wrap hero">
        <p class="hero__kicker">One manifest · two surfaces</p>
        <h1 class="hero__h">Your website, as <em>tools</em> agents can call.</h1>
        <p class="hero__sub">
          Paste a URL. ClawHarbor reads the site's palette and the verbs a customer can perform,
          executes every proposed tool against the live origin, and publishes what survived.
        </p>

        ${resumeHost
          ? html`
              <div class="resume">
                <p class="resume__p">
                  Signed in. Ready to crawl <b>${resumeHost}</b> — the address you started with.
                </p>
                <button type="button" class="btn" onClick=${() => actions.resume()}>
                  Crawl ${resumeHost}
                </button>
              </div>
            `
          : null}

        <form
          class="field"
          onSubmit=${(e: Event) => {
            e.preventDefault();
            actions.submit();
          }}
        >
          <label class="sr" for="site-url">Website address</label>
          <div class=${state.inputError ? 'field__row field__row--bad' : 'field__row'}>
            <span class="field__scheme" aria-hidden="true">https://</span>
            <input
              id="site-url"
              class="field__input"
              type="text"
              inputmode="url"
              autocomplete="url"
              spellcheck="false"
              autocapitalize="off"
              placeholder="cinestar.hr"
              aria-describedby="field-hint"
              aria-invalid=${state.inputError ? 'true' : 'false'}
              value=${state.input}
              onInput=${(e: Event) => actions.setInput((e.target as HTMLInputElement).value)}
            />
            <button class="btn btn--in-field" type="submit" disabled=${state.starting}>
              ${state.starting ? 'Starting…' : gated ? 'Continue' : 'Crawl it'}
            </button>
          </div>
          ${state.inputError ? html`<p class="field__error" role="alert">${state.inputError}</p>` : null}
          <p class="field__hint" id="field-hint">
            Read-only · 1 request per second · robots.txt honoured · never behind a login${gated
              ? ' · a free account, by email link'
              : ''}
          </p>

          <fieldset class="ownership" style="border:0;margin:18px 0 0;padding:0;min-width:0">
            <legend class="secret__label">Whose site is this?</legend>
            ${OwnershipChoice({
              value: 'third-party',
              current: state.ownership,
              head: 'Someone else’s site',
              body: html`Crawled under third-party rules and headed with the “Unofficial” disclaimer.
                The operator publishes it once the brand’s permission is on record.`,
              onPick: actions.setOwnership,
            })}
            ${OwnershipChoice({
              value: 'self-owned',
              current: state.ownership,
              head: 'This is my site',
              body: html`No disclaimer. Publishing waits until you have proved control of the domain
                — a DNS record, a file, or a meta tag; the gate shows all three.`,
              onPick: actions.setOwnership,
            })}
          </fieldset>

          <p class="examples__cap">
            ${state.mode === 'demo'
              ? 'Four recorded crawls. Each replays its own run, including the two that produced nothing.'
              : 'Four sites already crawled, if you want to see the shape of the answer first.'}
          </p>
          <ul class="examples">
            ${EXAMPLES.map(
              (ex) => html`
                <li>
                  <button type="button" class="example" onClick=${() => actions.useExample(ex.url)}>
                    <span class="example__url">${ex.label}</span>
                    <span class="example__note">${ex.note}</span>
                  </button>
                </li>
              `,
            )}
          </ul>
          ${state.mode === 'demo'
            ? html`<p class="examples__foot">
                Any other address replays the cinema recording — this build has no onboarding API to
                talk to, and pretending otherwise is the one thing this console will not do.
              </p>`
            : null}
        </form>
      </header>

      <section class="wrap section">
        <p class="eyebrow">What comes back</p>
        <div class="pair">
          <div class="pane">
            <span class="pane__tag">Surface 1 — WebMCP</span>
            <h2 class="pane__h">A branded page an agent can drive</h2>
            <p class="pane__p">
              Hosted in your own palette and type. It registers your tools on${SP}
              <code>document.modelContext</code>, so a browser agent calls them and the page
              repaints in front of the person watching. No extension, no plugin, no SDK.
            </p>
            <p class="pane__code">clawharbor.app/t/your-site</p>
          </div>
          <div class="pane">
            <span class="pane__tag">Surface 2 — MCP Apps</span>
            <h2 class="pane__h">A hosted MCP endpoint</h2>
            <p class="pane__p">
              A remote MCP server with predeclared <code>ui://</code> resources, so a chat client
              renders your widgets — your colours, your radii — instead of a wall of JSON. Paste one
              URL into the client and it is connected.
            </p>
            <p class="pane__code">clawharbor.app/t/your-site/mcp</p>
          </div>
        </div>
      </section>

      <section class="wrap section">
        <p class="eyebrow">And one thing that does not</p>
        <div class="refusal">
          <span class="stamp stamp--turned">Turned back</span>
          <p class="refusal__p">
            Every proposed tool is <strong>executed against your live site before it ships</strong>.
            Anything that returns no rows is dropped, and the reason is printed on this page rather
            than swallowed. On a 16-cinema chain the crawler found an undocumented JSON endpoint and
            pulled 61 real rows through it — then turned back the endpoint sitting next to it,
            because nine values across three vocabularies produced nothing. You publish the first
            one. You are told about the second.
          </p>
        </div>
      </section>
    </div>
  `;
}

/** The host alone: the band is answering "which site", not printing a URL. */
function hostOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}
